import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock,
  Mail,
  Phone,
  Calendar,
  MessageSquare,
  RefreshCw,
  Trash2,
  LogOut,
  ShieldCheck,
  ArrowLeft,
  CheckCircle2,
  TrendingUp,
  Download,
  Briefcase,
  ExternalLink,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";
import useSeo from "@/hooks/useSeo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "iw_admin_pass";

const STATUS_OPTIONS = [
  { v: "new", label: "New" },
  { v: "contacted", label: "Contacted" },
  { v: "won", label: "Won" },
  { v: "lost", label: "Lost" },
];

const STATUS_STYLES = {
  new: { bg: "rgba(212,175,55,0.15)", bd: "rgba(212,175,55,0.55)", fg: "var(--iw-gold)" },
  contacted: { bg: "rgba(100,149,237,0.14)", bd: "rgba(100,149,237,0.55)", fg: "#9cb8e6" },
  won: { bg: "rgba(46,204,113,0.14)", bd: "rgba(46,204,113,0.55)", fg: "#86d9a7" },
  lost: { bg: "rgba(200,80,80,0.14)", bd: "rgba(200,80,80,0.55)", fg: "#e6a0a0" },
  shortlisted: { bg: "rgba(100,149,237,0.14)", bd: "rgba(100,149,237,0.55)", fg: "#9cb8e6" },
  interviewed: { bg: "rgba(180,130,220,0.14)", bd: "rgba(180,130,220,0.55)", fg: "#c7a7e6" },
  hired: { bg: "rgba(46,204,113,0.14)", bd: "rgba(46,204,113,0.55)", fg: "#86d9a7" },
  rejected: { bg: "rgba(200,80,80,0.14)", bd: "rgba(200,80,80,0.55)", fg: "#e6a0a0" },
};

const CAREER_STATUS_OPTIONS = [
  { v: "new", label: "New" },
  { v: "shortlisted", label: "Shortlisted" },
  { v: "interviewed", label: "Interviewed" },
  { v: "hired", label: "Hired" },
  { v: "rejected", label: "Rejected" },
];

export default function AdminPage() {
  useSeo({
    title: "Admin · InvitationWala",
    description: "Internal admin dashboard.",
    canonical: "https://www.invitationwala.online/admin",
    noindex: true,
  });
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [careers, setCareers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [careerFilter, setCareerFilter] = useState("all");
  const [tab, setTab] = useState("leads"); // "leads" | "careers"

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      setPassword(stored);
      verify(stored, true);
    }
  }, []);

  const authHeader = (pw) => ({ headers: { "X-Admin-Password": pw } });

  const verify = async (pw, silent = false) => {
    try {
      setLoading(true);
      await axios.post(`${API}/admin/login`, { password: pw });
      localStorage.setItem(STORAGE_KEY, pw);
      setAuthed(true);
      await Promise.all([loadLeads(pw), loadCareers(pw)]);
      if (!silent) toast.success("Welcome back, founder.");
    } catch (err) {
      if (!silent) toast.error("Incorrect password.");
      localStorage.removeItem(STORAGE_KEY);
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  const loadLeads = async (pw = password) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/leads`, authHeader(pw));
      setLeads(res.data || []);
    } catch (err) {
      toast.error("Failed to load leads.");
      if (err?.response?.status === 401) logout();
    } finally {
      setLoading(false);
    }
  };

  const loadCareers = async (pw = password) => {
    try {
      const res = await axios.get(`${API}/admin/careers`, authHeader(pw));
      setCareers(res.data || []);
    } catch (err) {
      // Silent — careers might be empty, don't spam errors
      if (err?.response?.status === 401) logout();
    }
  };

  const refreshAll = async () => {
    await Promise.all([loadLeads(), loadCareers()]);
  };

  const updateCareerStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/admin/careers/${id}?status=${status}`,
        {},
        authHeader(password)
      );
      setCareers((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success(`Marked as ${status}.`);
    } catch (err) {
      toast.error("Update failed.");
    }
  };

  const removeCareer = async (id) => {
    if (!window.confirm("Delete this application permanently?")) return;
    try {
      await axios.delete(`${API}/admin/careers/${id}`, authHeader(password));
      setCareers((ls) => ls.filter((l) => l.id !== id));
      toast.success("Application deleted.");
    } catch (err) {
      toast.error("Delete failed.");
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/admin/leads/${id}?status=${status}`,
        {},
        authHeader(password)
      );
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success(`Marked as ${status}.`);
    } catch (err) {
      toast.error("Update failed.");
    }
  };

  const removeLead = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await axios.delete(`${API}/admin/leads/${id}`, authHeader(password));
      setLeads((ls) => ls.filter((l) => l.id !== id));
      toast.success("Lead deleted.");
    } catch (err) {
      toast.error("Delete failed.");
    }
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setAuthed(false);
    setLeads([]);
    setCareers([]);
    setPassword("");
  };

  const exportCsv = () => {
    const rows = (filter === "all" ? leads : leads.filter((l) => l.status === filter));
    if (rows.length === 0) {
      toast.error("Nothing to export in current view.");
      return;
    }
    const headers = [
      "Name",
      "Phone",
      "Email",
      "Event Type",
      "Preferred Date",
      "Message",
      "Status",
      "Received At",
    ];
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      return `"${s.replace(/"/g, '""')}"`;
    };
    const body = rows
      .map((l) =>
        [
          l.name,
          l.phone,
          l.email,
          l.event_type,
          l.preferred_date || "",
          l.message || "",
          l.status,
          l.created_at,
        ]
          .map(esc)
          .join(",")
      )
      .join("\n");
    const csv = headers.map(esc).join(",") + "\n" + body;
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const today = new Date().toISOString().slice(0, 10);
    const scope = filter === "all" ? "all" : filter;
    a.href = url;
    a.download = `invitation-wala-leads-${scope}-${today}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} lead${rows.length === 1 ? "" : "s"}.`);
  };

  const filtered =
    filter === "all" ? leads : leads.filter((l) => l.status === filter);

  const counts = leads.reduce(
    (acc, l) => ({ ...acc, [l.status]: (acc[l.status] || 0) + 1 }),
    {}
  );

  const filteredCareers =
    careerFilter === "all"
      ? careers
      : careers.filter((c) => c.status === careerFilter);

  const careerCounts = careers.reduce(
    (acc, c) => ({ ...acc, [c.status]: (acc[c.status] || 0) + 1 }),
    {}
  );

  // Build 30-day trend data (daily lead counts)
  const trend = useMemo(() => {
    const days = 30;
    const buckets = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      buckets.push({ date: d, key: d.toISOString().slice(0, 10), count: 0 });
    }
    const map = Object.fromEntries(buckets.map((b) => [b.key, b]));
    leads.forEach((l) => {
      if (!l.created_at) return;
      const key = new Date(l.created_at).toISOString().slice(0, 10);
      if (map[key]) map[key].count += 1;
    });
    return buckets;
  }, [leads]);

  // --- Login screen ---
  if (!authed) {
    return (
      <div
        className="min-h-screen grid place-items-center p-6"
        style={{
          background:
            "radial-gradient(ellipse at 20% 10%, rgba(212,175,55,0.08) 0%, transparent 50%), radial-gradient(ellipse at 80% 90%, rgba(232,165,180,0.08) 0%, transparent 50%), #0a0a0a",
        }}
        data-testid="admin-login-page"
      >
        <div
          className="w-full max-w-md p-8 md:p-10"
          style={{
            background:
              "linear-gradient(180deg, rgba(26,20,20,0.9) 0%, rgba(20,16,16,0.9) 100%)",
            border: "1px solid rgba(212,175,55,0.35)",
            backdropFilter: "blur(14px)",
            boxShadow: "0 40px 100px -10px rgba(232,165,180,0.25)",
          }}
        >
          <Link
            to="/"
            className="inline-flex items-center gap-2 overline text-[var(--iw-faint)] hover:text-[var(--iw-gold)] transition-colors"
            data-testid="admin-back-link"
          >
            <ArrowLeft size={12} /> Back to site
          </Link>
          <div className="flex items-center gap-3 mt-6 mb-2">
            <ShieldCheck size={22} className="text-[var(--iw-gold)]" strokeWidth={1.5} />
            <span className="overline text-[var(--iw-gold)]">Atelier · Private</span>
          </div>
          <h1
            className="font-serif-display text-[var(--iw-ivory)] text-4xl md:text-5xl leading-tight"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            The <span className="italic gold-rose-gradient">inner circle</span>.
          </h1>
          <p className="text-[var(--iw-muted)] text-sm mt-4 leading-relaxed">
            Enter your founder passphrase to view every request that has walked
            through our doors.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!password) return;
              verify(password);
            }}
            className="mt-8 space-y-5"
          >
            <label className="flex flex-col gap-2">
              <span className="overline text-[var(--iw-faint)]">Passphrase</span>
              <div className="relative">
                <Lock
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--iw-faint)]"
                />
                <input
                  type="password"
                  className="input-luxe pl-10"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid="admin-password-input"
                  autoFocus
                />
              </div>
            </label>
            <button
              type="submit"
              disabled={loading || !password}
              className="btn-gold w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="admin-login-submit"
            >
              {loading ? "Verifying…" : "Unlock"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // --- Dashboard ---
  return (
    <div
      className="min-h-screen"
      style={{
        background:
          "radial-gradient(ellipse at 50% 0%, rgba(212,175,55,0.05) 0%, transparent 60%), #0a0a0a",
      }}
      data-testid="admin-dashboard"
    >
      {/* Header */}
      <header
        className="sticky top-0 z-30 px-5 md:px-10 py-4 md:py-5 flex flex-col md:flex-row md:items-center justify-between gap-4"
        style={{
          background: "rgba(10,10,10,0.85)",
          backdropFilter: "blur(14px)",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <div className="flex items-center gap-3">
          <ShieldCheck size={18} className="text-[var(--iw-gold)]" strokeWidth={1.5} />
          <div>
            <div className="overline text-[var(--iw-gold)]">
              Atelier · {tab === "leads" ? "Leads" : "Applications"}
            </div>
            <h2
              className="font-serif-display text-[var(--iw-ivory)] text-xl md:text-2xl leading-none mt-1"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
            >
              {tab === "leads" ? "The Ledger" : "The Atelier Roster"}
            </h2>
          </div>
        </div>

        {/* Tab switcher */}
        <div
          className="inline-flex self-start md:self-auto"
          style={{
            border: "1px solid rgba(255,255,255,0.1)",
          }}
          data-testid="admin-tabs"
        >
          <button
            onClick={() => setTab("leads")}
            data-testid="admin-tab-leads"
            className="px-4 py-2 overline text-xs flex items-center gap-2 transition-colors"
            style={{
              background:
                tab === "leads"
                  ? "linear-gradient(135deg, rgba(212,175,55,0.18) 0%, rgba(232,165,180,0.12) 100%)"
                  : "transparent",
              color: tab === "leads" ? "var(--iw-gold)" : "var(--iw-faint)",
              borderRight: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <Users size={12} /> Leads
            <span className="text-[10px] opacity-70">{leads.length}</span>
          </button>
          <button
            onClick={() => setTab("careers")}
            data-testid="admin-tab-careers"
            className="px-4 py-2 overline text-xs flex items-center gap-2 transition-colors"
            style={{
              background:
                tab === "careers"
                  ? "linear-gradient(135deg, rgba(232,165,180,0.18) 0%, rgba(212,175,55,0.12) 100%)"
                  : "transparent",
              color: tab === "careers" ? "var(--iw-rose)" : "var(--iw-faint)",
            }}
          >
            <Briefcase size={12} /> Careers
            <span className="text-[10px] opacity-70">{careers.length}</span>
          </button>
        </div>

        <div className="flex items-center gap-2 md:gap-3 flex-wrap">
          {tab === "leads" && (
            <button
              onClick={exportCsv}
              className="btn-ghost-gold text-xs"
              data-testid="admin-export-csv"
              disabled={leads.length === 0}
            >
              <Download size={12} />
              Export CSV
            </button>
          )}
          <button
            onClick={refreshAll}
            className="btn-ghost-gold text-xs"
            data-testid="admin-refresh"
            disabled={loading}
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
          <button
            onClick={logout}
            className="btn-ghost-gold text-xs"
            data-testid="admin-logout"
          >
            <LogOut size={12} />
            Sign out
          </button>
        </div>
      </header>

      {/* Stats + filters */}
      <div className="px-5 md:px-10 py-8 md:py-10 max-w-[1600px] mx-auto">
        {tab === "leads" && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4 mb-8">
              <StatCard
                label="Total"
                value={leads.length}
                active={filter === "all"}
                onClick={() => setFilter("all")}
                testid="admin-filter-all"
              />
              {STATUS_OPTIONS.map((s) => (
                <StatCard
                  key={s.v}
                  label={s.label}
                  value={counts[s.v] || 0}
                  active={filter === s.v}
                  onClick={() => setFilter(s.v)}
                  accent={STATUS_STYLES[s.v].fg}
                  testid={`admin-filter-${s.v}`}
                />
              ))}
            </div>

            {/* 30-day trend chart */}
            <TrendChart data={trend} total={leads.length} />

            {/* Leads list */}
            {loading && leads.length === 0 ? (
              <div className="text-center py-24 text-[var(--iw-muted)]">
                Loading the ledger…
              </div>
            ) : filtered.length === 0 ? (
              <div
                className="text-center py-24"
                style={{ border: "1px dashed rgba(212,175,55,0.25)" }}
              >
                <CheckCircle2
                  size={40}
                  strokeWidth={1}
                  className="text-[var(--iw-gold)] mx-auto mb-4"
                />
                <p
                  className="font-serif-display text-[var(--iw-ivory)] text-2xl italic"
                  style={{ fontFamily: "Cormorant Garamond, serif" }}
                >
                  Nothing here yet.
                </p>
                <p className="text-[var(--iw-muted)] text-sm mt-2">
                  {filter === "all"
                    ? "New enquiries will arrive here the moment they're submitted."
                    : `No leads marked "${filter}".`}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filtered.map((l) => (
                  <LeadRow
                    key={l.id}
                    lead={l}
                    onStatus={updateStatus}
                    onDelete={removeLead}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {tab === "careers" && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 md:gap-4 mb-8">
              <StatCard
                label="Total"
                value={careers.length}
                active={careerFilter === "all"}
                onClick={() => setCareerFilter("all")}
                testid="admin-career-filter-all"
              />
              {CAREER_STATUS_OPTIONS.map((s) => (
                <StatCard
                  key={s.v}
                  label={s.label}
                  value={careerCounts[s.v] || 0}
                  active={careerFilter === s.v}
                  onClick={() => setCareerFilter(s.v)}
                  accent={STATUS_STYLES[s.v]?.fg}
                  testid={`admin-career-filter-${s.v}`}
                />
              ))}
            </div>

            {filteredCareers.length === 0 ? (
              <div
                className="text-center py-24"
                style={{ border: "1px dashed rgba(232,165,180,0.25)" }}
              >
                <Briefcase
                  size={40}
                  strokeWidth={1}
                  className="text-[var(--iw-rose)] mx-auto mb-4"
                />
                <p
                  className="font-serif-display text-[var(--iw-ivory)] text-2xl italic"
                  style={{ fontFamily: "Cormorant Garamond, serif" }}
                >
                  No applications yet.
                </p>
                <p className="text-[var(--iw-muted)] text-sm mt-2">
                  {careerFilter === "all"
                    ? "Share /careers on Instagram and stories to attract talent."
                    : `No applications marked "${careerFilter}".`}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredCareers.map((c) => (
                  <CareerRow
                    key={c.id}
                    item={c}
                    onStatus={updateCareerStatus}
                    onDelete={removeCareer}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// --- Small components ---
const TrendChart = ({ data, total }) => {
  const max = Math.max(1, ...data.map((d) => d.count));
  const peak = data.reduce((acc, d) => (d.count > acc.count ? d : acc), data[0]);
  const sum = data.reduce((a, d) => a + d.count, 0);
  const avg = (sum / data.length).toFixed(1);

  return (
    <div
      className="mb-8 p-5 md:p-6"
      style={{
        background: "rgba(255,255,255,0.02)",
        border: "1px solid rgba(255,255,255,0.08)",
      }}
      data-testid="admin-trend-chart"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div className="flex items-center gap-3">
          <TrendingUp size={16} className="text-[var(--iw-gold)]" strokeWidth={1.5} />
          <div>
            <div className="overline text-[var(--iw-gold)]">Enquiries · last 30 days</div>
            <h3
              className="font-serif-display text-[var(--iw-ivory)] text-2xl leading-none mt-1"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
            >
              The <span className="italic">curve</span>.
            </h3>
          </div>
        </div>
        <div className="flex items-center gap-5 text-right">
          <div>
            <div className="overline text-[var(--iw-faint)]">In window</div>
            <div
              className="font-serif-display text-[var(--iw-ivory)] text-2xl leading-none"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
            >
              {sum}
            </div>
          </div>
          <div>
            <div className="overline text-[var(--iw-faint)]">Peak day</div>
            <div
              className="font-serif-display text-[var(--iw-ivory)] text-2xl leading-none"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
            >
              {peak?.count || 0}
            </div>
          </div>
          <div>
            <div className="overline text-[var(--iw-faint)]">Avg/day</div>
            <div
              className="font-serif-display text-[var(--iw-ivory)] text-2xl leading-none"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
            >
              {avg}
            </div>
          </div>
        </div>
      </div>

      {/* Bars */}
      <div className="flex items-end gap-[3px] h-28 md:h-32 w-full">
        {data.map((d) => {
          const h = (d.count / max) * 100;
          const barHeight = d.count === 0 ? 2 : Math.max(h, 8);
          const label = d.date.toLocaleDateString(undefined, {
            day: "numeric",
            month: "short",
          });
          return (
            <div
              key={d.key}
              className="group relative flex-1 h-full flex items-end min-w-0"
              title={`${label} · ${d.count} lead${d.count === 1 ? "" : "s"}`}
            >
              <div
                className="w-full transition-all duration-500"
                style={{
                  height: `${barHeight}%`,
                  background:
                    d.count === 0
                      ? "rgba(255,255,255,0.06)"
                      : "linear-gradient(180deg, #E8A5B4 0%, #D4AF37 100%)",
                  boxShadow:
                    d.count > 0
                      ? "0 0 12px -2px rgba(232,165,180,0.5)"
                      : "none",
                  minHeight: d.count > 0 ? "4px" : "2px",
                }}
              />
              {/* Count label on bars with value */}
              {d.count > 0 && (
                <span
                  className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] text-[var(--iw-gold)]"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  {d.count}
                </span>
              )}
              {/* Hover tooltip */}
              <div
                className="absolute bottom-full left-1/2 -translate-x-1/2 mb-6 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 px-2 py-1 text-[10px]"
                style={{
                  background: "#0a0a0a",
                  border: "1px solid rgba(212,175,55,0.4)",
                  color: "var(--iw-ivory)",
                  fontFamily: "Outfit, sans-serif",
                }}
              >
                {label} · <span className="text-[var(--iw-gold)]">{d.count}</span>
              </div>
            </div>
          );
        })}
      </div>
      {/* Baseline */}
      <div className="flex justify-between mt-2 overline text-[var(--iw-faint)] text-[9px]">
        <span>
          {data[0]?.date.toLocaleDateString(undefined, { day: "numeric", month: "short" })}
        </span>
        <span>Today</span>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, active, onClick, accent, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="text-left p-4 md:p-5 transition-all"
    style={{
      background: active
        ? "linear-gradient(135deg, rgba(212,175,55,0.14) 0%, rgba(232,165,180,0.1) 100%)"
        : "rgba(255,255,255,0.02)",
      border: active
        ? "1px solid rgba(212,175,55,0.55)"
        : "1px solid rgba(255,255,255,0.08)",
    }}
  >
    <div
      className="overline"
      style={{ color: accent || "var(--iw-faint)" }}
    >
      {label}
    </div>
    <div
      className="font-serif-display text-3xl md:text-4xl text-[var(--iw-ivory)] mt-1 leading-none"
      style={{ fontFamily: "Cormorant Garamond, serif" }}
    >
      {value}
    </div>
  </button>
);

const LeadRow = ({ lead, onStatus, onDelete }) => {
  const s = STATUS_STYLES[lead.status] || STATUS_STYLES.new;
  const created = new Date(lead.created_at);
  const when = isNaN(created.getTime())
    ? ""
    : created.toLocaleString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });

  return (
    <div
      className="p-5 md:p-6 flex flex-col md:flex-row md:items-start gap-5 transition-colors"
      style={{
        background: "rgba(255,255,255,0.02)",
        border: "1px solid rgba(255,255,255,0.06)",
      }}
      data-testid={`admin-lead-${lead.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <h3
            className="font-serif-display text-[var(--iw-ivory)] text-2xl leading-none"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            {lead.name}
          </h3>
          <span
            className="overline px-2.5 py-0.5 text-[10px]"
            style={{
              background: s.bg,
              border: `1px solid ${s.bd}`,
              color: s.fg,
            }}
          >
            {lead.status}
          </span>
          <span className="overline text-[var(--iw-faint)]">
            {lead.event_type}
          </span>
        </div>
        <div className="grid sm:grid-cols-2 gap-2 text-sm text-[var(--iw-ivory-dim)]">
          <a
            href={`tel:${lead.phone}`}
            className="flex items-center gap-2 hover:text-[var(--iw-gold)] transition-colors"
          >
            <Phone size={13} className="text-[var(--iw-faint)]" />
            {lead.phone}
          </a>
          <a
            href={`mailto:${lead.email}`}
            className="flex items-center gap-2 hover:text-[var(--iw-gold)] transition-colors truncate"
          >
            <Mail size={13} className="text-[var(--iw-faint)]" />
            <span className="truncate">{lead.email}</span>
          </a>
          {lead.preferred_date && (
            <div className="flex items-center gap-2">
              <Calendar size={13} className="text-[var(--iw-faint)]" />
              {lead.preferred_date}
            </div>
          )}
          <div className="flex items-center gap-2 text-[var(--iw-faint)]">
            <Calendar size={13} />
            <span>Received {when}</span>
          </div>
        </div>
        {lead.message && (
          <div className="mt-3 flex items-start gap-2 text-sm text-[var(--iw-muted)] italic">
            <MessageSquare size={13} className="text-[var(--iw-faint)] mt-1 shrink-0" />
            <span>"{lead.message}"</span>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2 md:w-48 shrink-0">
        <div className="flex flex-wrap gap-1.5">
          {STATUS_OPTIONS.map((opt) => {
            const st = STATUS_STYLES[opt.v];
            const active = lead.status === opt.v;
            return (
              <button
                key={opt.v}
                onClick={() => onStatus(lead.id, opt.v)}
                disabled={active}
                data-testid={`admin-status-${lead.id}-${opt.v}`}
                className="text-[10px] overline px-2.5 py-1 transition-all"
                style={{
                  background: active ? st.bg : "transparent",
                  border: `1px solid ${active ? st.bd : "rgba(255,255,255,0.14)"}`,
                  color: active ? st.fg : "var(--iw-faint)",
                  cursor: active ? "default" : "pointer",
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <a
          href={`https://wa.me/${lead.phone.replace(/[^\d]/g, "")}`}
          target="_blank"
          rel="noreferrer"
          className="btn-ghost-gold text-xs justify-center"
          data-testid={`admin-wa-${lead.id}`}
        >
          Open WhatsApp
        </a>
        <button
          onClick={() => onDelete(lead.id)}
          className="text-[10px] overline px-2.5 py-1 text-[var(--iw-faint)] hover:text-red-400 transition-colors flex items-center justify-center gap-1.5"
          style={{ border: "1px solid rgba(255,255,255,0.1)" }}
          data-testid={`admin-delete-${lead.id}`}
        >
          <Trash2 size={11} /> Delete
        </button>
      </div>
    </div>
  );
};


const CareerRow = ({ item, onStatus, onDelete }) => {
  const s = STATUS_STYLES[item.status] || STATUS_STYLES.new;
  const created = new Date(item.created_at);
  const when = isNaN(created.getTime())
    ? ""
    : created.toLocaleString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });

  return (
    <div
      className="p-5 md:p-6 flex flex-col md:flex-row md:items-start gap-5 transition-colors"
      style={{
        background: "rgba(255,255,255,0.02)",
        border: "1px solid rgba(232,165,180,0.15)",
      }}
      data-testid={`admin-career-${item.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <h3
            className="font-serif-display text-[var(--iw-ivory)] text-2xl leading-none"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            {item.name}
          </h3>
          <span
            className="overline px-2.5 py-0.5 text-[10px]"
            style={{
              background: s.bg,
              border: `1px solid ${s.bd}`,
              color: s.fg,
            }}
          >
            {item.status}
          </span>
          <span className="overline text-[var(--iw-rose)]">
            <Briefcase size={10} className="inline mr-1" /> {item.role}
          </span>
          <span className="overline text-[var(--iw-faint)]">
            {item.experience}
          </span>
        </div>
        <div className="grid sm:grid-cols-2 gap-2 text-sm text-[var(--iw-ivory-dim)]">
          <a
            href={`tel:${item.phone}`}
            className="flex items-center gap-2 hover:text-[var(--iw-gold)] transition-colors"
          >
            <Phone size={13} className="text-[var(--iw-faint)]" />
            {item.phone}
          </a>
          <a
            href={`mailto:${item.email}`}
            className="flex items-center gap-2 hover:text-[var(--iw-gold)] transition-colors truncate"
          >
            <Mail size={13} className="text-[var(--iw-faint)]" />
            <span className="truncate">{item.email}</span>
          </a>
          {item.portfolio_url && (
            <a
              href={item.portfolio_url}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 hover:text-[var(--iw-rose)] transition-colors truncate sm:col-span-2"
            >
              <ExternalLink size={13} className="text-[var(--iw-faint)]" />
              <span className="truncate">{item.portfolio_url}</span>
            </a>
          )}
          <div className="flex items-center gap-2 text-[var(--iw-faint)]">
            <Calendar size={13} />
            <span>Applied {when}</span>
          </div>
        </div>
        {item.cover_note && (
          <div className="mt-3 flex items-start gap-2 text-sm text-[var(--iw-muted)] italic">
            <MessageSquare size={13} className="text-[var(--iw-faint)] mt-1 shrink-0" />
            <span>"{item.cover_note}"</span>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2 md:w-52 shrink-0">
        <div className="flex flex-wrap gap-1.5">
          {CAREER_STATUS_OPTIONS.map((opt) => {
            const st = STATUS_STYLES[opt.v];
            const active = item.status === opt.v;
            return (
              <button
                key={opt.v}
                onClick={() => onStatus(item.id, opt.v)}
                disabled={active}
                data-testid={`admin-career-status-${item.id}-${opt.v}`}
                className="text-[10px] overline px-2.5 py-1 transition-all"
                style={{
                  background: active ? st.bg : "transparent",
                  border: `1px solid ${active ? st.bd : "rgba(255,255,255,0.14)"}`,
                  color: active ? st.fg : "var(--iw-faint)",
                  cursor: active ? "default" : "pointer",
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <a
          href={`https://wa.me/${item.phone.replace(/[^\d]/g, "")}`}
          target="_blank"
          rel="noreferrer"
          className="btn-ghost-gold text-xs justify-center"
          data-testid={`admin-career-wa-${item.id}`}
        >
          Open WhatsApp
        </a>
        <button
          onClick={() => onDelete(item.id)}
          className="text-[10px] overline px-2.5 py-1 text-[var(--iw-faint)] hover:text-red-400 transition-colors flex items-center justify-center gap-1.5"
          style={{ border: "1px solid rgba(255,255,255,0.1)" }}
          data-testid={`admin-career-delete-${item.id}`}
        >
          <Trash2 size={11} /> Delete
        </button>
      </div>
    </div>
  );
};
